import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, AlertCircle, Mail, Phone, Calendar, MapPin, Package, CreditCard, FileText, Printer, Home, RefreshCw } from 'lucide-react';
import axios from 'axios';

const PaymentSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [orderDetails, setOrderDetails] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const orderId = params.get('order_id');
        const paymentStatus = params.get('payment_status');

        console.log('Payment Success Page - Order ID:', orderId);

        if (!orderId) {
          setStatus('error');
          setErrorMessage('No order ID found in the URL');
          return;
        }

        if (paymentStatus === 'failed' || paymentStatus === 'FAILED') {
          setStatus('error');
          setErrorMessage('Payment was not successful. Please try again.');
          return;
        }

        const isBikeBooking = orderId.startsWith('booking_') || (orderId.startsWith('kgv') && !orderId.startsWith('kgvk') && !orderId.startsWith('kgvh'));

        if (isBikeBooking) {
          const storedData = localStorage.getItem('pendingBookingOrderData');
          if (!storedData) {
            const sessionData = sessionStorage.getItem('pendingBookingOrderData');
            if (sessionData) {
              await processBikePaymentVerification(orderId, JSON.parse(sessionData));
            } else {
              await fetchBikeOrderDetails(orderId);
            }
          } else {
            await processBikePaymentVerification(orderId, JSON.parse(storedData));
          }
        } else {
          const storedData = localStorage.getItem('pendingOrderData');
          if (!storedData) {
            const sessionData = sessionStorage.getItem('pendingOrderData');
            if (sessionData) {
              await processPaymentVerification(orderId, JSON.parse(sessionData));
            } else {
              await fetchOrderDetails(orderId);
            }
          } else {
            await processPaymentVerification(orderId, JSON.parse(storedData));
          }
        }
      } catch (error) {
        console.error('Payment verification error:', error);
        setStatus('error');
        setErrorMessage(error.message || 'Payment verification failed');
      }
    };

    const processBikePaymentVerification = async (orderId, orderData) => {
      console.log('Processing bike payment verification for order:', orderId);
      console.log('Order data:', orderData);

      if (orderData.orderId !== orderId) {
        console.warn('Order ID mismatch:', orderData.orderId, '!=', orderId);
      }

      try {
        const apiUrl = 'https://auth.kgvl.co.in';
        const verifyResponse = await axios.post(`${apiUrl}/api/bike-bookings/verify`, {
          orderId: orderId,
          name: orderData.name,
          phone: orderData.phone,
          email: orderData.email,
          idProof: orderData.idProof,
          idNumber: orderData.idNumber,
          vehicleNo: orderData.vehicleNo,
          model: orderData.model,
          bikeCC: orderData.bikeCC,
          chassisNo: orderData.chassisNo,
          chargerType: orderData.chargerType,
          batteryType: orderData.batteryType,
          batteryCapacity: orderData.batteryCapacity,
          country: orderData.country,
          state: orderData.state,
          city: orderData.city,
          address1: orderData.address1,
          address2: orderData.address2,
          pinCode: orderData.pinCode,
          landmark: orderData.landmark,
          color: orderData.color,
          bookingAmount: orderData.bookingAmount,
          totalCost: orderData.totalCost
        });

        console.log('Verify bike payment response:', verifyResponse.data);

        if (verifyResponse.data.success) {
          setStatus('success');
          const order = verifyResponse.data.order;
          setOrderDetails({
            orderId: orderId,
            paymentId: order?.cashfree_payment_id || verifyResponse.data.paymentStatus || 'SUCCESS',
            amount: orderData.bookingAmount,
            name: orderData.name,
            email: orderData.email,
            phone: orderData.phone,
            model: orderData.model,
            city: orderData.city,
            message: 'Your bike booking has been processed successfully!',
            timestamp: new Date().toLocaleString(),
            ...order
          });

          localStorage.removeItem('pendingBookingOrderData');
          sessionStorage.removeItem('pendingBookingOrderData');
        } else {
          setStatus('error');
          setErrorMessage(verifyResponse.data.message || 'Payment verification failed');
        }
      } catch (error) {
        console.error('Bike Verification API error:', error);
        if (error.code === 'ERR_NETWORK') {
          setErrorMessage('Unable to connect to server. Please check your internet connection.');
        } else {
          setErrorMessage(error.response?.data?.message || error.message || 'Payment verification failed');
        }
        setStatus('error');
      }
    };

    const fetchBikeOrderDetails = async (orderId) => {
      try {
        const apiUrl = 'https://auth.kgvl.co.in';
        const response = await axios.get(`${apiUrl}/api/bike-bookings/cashfree/${orderId}`);

        if (response.data.success) {
          const order = response.data.order;
          setStatus('success');
          setOrderDetails({
            orderId: order.cashfree_order_id,
            paymentId: order.cashfree_payment_id,
            amount: order.bookingAmount || order.payment_amount,
            name: order.name,
            email: order.email,
            phone: order.phone,
            model: order.model,
            city: order.city,
            message: 'Bike booking payment verified and processed!',
            timestamp: new Date(order.transaction_date).toLocaleString(),
            ...order
          });
        } else {
          setStatus('error');
          setErrorMessage('Bike booking order not found. Please contact support.');
        }
      } catch (error) {
        console.error('Fetch bike order error:', error);
        setStatus('error');
        setErrorMessage('Unable to fetch bike order details. Please contact support.');
      }
    };

    const processPaymentVerification = async (orderId, orderData) => {
      console.log('Processing payment verification for order:', orderId);
      console.log('Order data:', orderData);

      if (orderData.orderId !== orderId) {
        console.warn('Order ID mismatch:', orderData.orderId, '!=', orderId);
      }

      try {
        const apiUrl = 'https://auth.kgvl.co.in';
        const verifyResponse = await axios.post(`${apiUrl}/api/order/verify`, {
          orderId: orderId,
          name: orderData.name,
          phone: orderData.phone,
          email: orderData.email,
          idProof: orderData.idProof,
          idNumber: orderData.idNumber,
          vehicleNo: orderData.vehicleNo,
          model: orderData.model,
          bikeCC: orderData.bikeCC,
          chassisNo: orderData.chassisNo,
          chargerType: orderData.chargerType,
          batteryType: orderData.batteryType,
          batteryCapacity: orderData.batteryCapacity,
          country: orderData.country,
          state: orderData.state,
          city: orderData.city,
          branch: orderData.city,
          quantity: orderData.quantity || 1
        });

        console.log('Verify payment response:', verifyResponse.data);

        if (verifyResponse.data.success) {
          setStatus('success');
          setOrderDetails({
            orderId: orderId,
            paymentId: verifyResponse.data.order?.cashfree_payment_id,
            amount: verifyResponse.data.order?.payment_amount,
            name: orderData.name,
            email: orderData.email,
            phone: orderData.phone,
            model: orderData.model,
            city: orderData.city,
            message: 'Your payment has been processed successfully!',
            timestamp: new Date().toLocaleString(),
            ...verifyResponse.data.order
          });

          localStorage.removeItem('pendingOrderData');
          sessionStorage.removeItem('pendingOrderData');

          await sendConfirmationEmail(orderData, orderId);
        } else {
          setStatus('error');
          setErrorMessage(verifyResponse.data.message || 'Payment verification failed');
        }
      } catch (error) {
        console.error('Verification API error:', error);
        if (error.code === 'ERR_NETWORK') {
          setErrorMessage('Unable to connect to server. Please check your internet connection.');
        } else {
          setErrorMessage(error.response?.data?.message || error.message || 'Payment verification failed');
        }
        setStatus('error');
      }
    };

    const fetchOrderDetails = async (orderId) => {
      try {
        const apiUrl = 'https://auth.kgvl.co.in';
        const response = await axios.get(`${apiUrl}/api/order/get-order/${orderId}`);

        if (response.data.success) {
          const order = response.data.order;
          setStatus('success');
          setOrderDetails({
            orderId: order.cashfree_order_id,
            paymentId: order.cashfree_payment_id,
            amount: order.payment_amount,
            name: order.name,
            email: order.email,
            phone: order.phone,
            model: order.model,
            city: order.city,
            message: 'Payment already verified and processed!',
            timestamp: new Date(order.transaction_date).toLocaleString(),
            ...order
          });
        } else {
          setStatus('error');
          setErrorMessage('Order not found. Please contact support.');
        }
      } catch (error) {
        console.error('Fetch order error:', error);
        setStatus('error');
        setErrorMessage('Unable to fetch order details. Please contact support.');
      }
    };

    const sendConfirmationEmail = async (orderData, orderId) => {
      try {
        const apiUrl = 'https://auth.kgvl.co.in';
        await axios.post(`${apiUrl}/api/order/send-confirmation`, {
          orderId,
          customerEmail: orderData.email,
          customerName: orderData.name,
          amount: orderData.payableAmount,
          model: orderData.model
        });
      } catch (error) {
        console.warn('Failed to send confirmation email:', error);
      }
    };

    verifyPayment();
  }, [location]);

  // Loading state
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-12 max-w-md w-full text-center">
          <Loader2 className="w-20 h-20 text-blue-600 animate-spin mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-gray-800 mb-3">Verifying Your Payment</h2>
          <p className="text-gray-600 mb-2">Please wait while we confirm your payment</p>
          <p className="text-sm text-gray-500">This may take a few moments</p>
        </div>
      </div>
    );
  }

  // Error state
  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-2xl w-full">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-4">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Payment Failed</h1>
            <p className="text-lg text-gray-600">{errorMessage}</p>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">
                If you were charged, please contact our support team with your order ID for assistance.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => navigate('/KgvkitOrder')}
              className="w-full bg-red-600 text-white px-6 py-4 rounded-lg hover:bg-red-700 transition-all font-semibold shadow-lg hover:shadow-xl"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full border-2 border-gray-300 text-gray-700 px-6 py-4 rounded-lg hover:bg-gray-50 transition-all font-semibold"
            >
              Go to Homepage
            </button>
            <button
              onClick={() => navigate('/contact')}
              className="w-full border-2 border-gray-300 text-gray-700 px-6 py-4 rounded-lg hover:bg-gray-50 transition-all font-semibold"
            >
              Contact Support
            </button>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-600">
              Need help? Email us at{' '}
              <a href="mailto:team@kgvl.co.in" className="text-red-600 hover:underline font-medium">
                team@kgvl.co.in
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Success Header */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6 text-center">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-green-100 rounded-full mb-6">
            <CheckCircle className="w-16 h-16 text-green-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Payment Successful</h1>
          <p className="text-xl text-gray-600">Your order has been confirmed</p>
        </div>

        {/* Order Information Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Order Summary */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-200">
              <Package className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-bold text-gray-900">Order Summary</h2>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">Order ID</p>
                <p className="text-base font-semibold text-gray-900">{orderDetails?.orderId}</p>
              </div>
              {orderDetails?.paymentId && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Payment ID</p>
                  <p className="text-base font-semibold text-gray-900">{orderDetails.paymentId}</p>
                </div>
              )}
              {orderDetails?.amount && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Amount Paid</p>
                  <p className="text-2xl font-bold text-green-600">
                    ₹{orderDetails.amount?.toLocaleString('en-IN')}
                  </p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-500 mb-1">Date & Time</p>
                <p className="text-base font-medium text-gray-900">{orderDetails?.timestamp}</p>
              </div>
            </div>
          </div>

          {/* Customer Details */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-200">
              <FileText className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-bold text-gray-900">Customer Details</h2>
            </div>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-700 font-semibold text-lg">
                    {orderDetails?.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-500">Name</p>
                  <p className="text-base font-semibold text-gray-900">{orderDetails?.name}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-gray-400 mt-1" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="text-base font-medium text-gray-900 break-all">{orderDetails?.email}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-gray-400 mt-1" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="text-base font-medium text-gray-900">{orderDetails?.phone}</p>
                </div>
              </div>
              {orderDetails?.city && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-gray-400 mt-1" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-500">Location</p>
                    <p className="text-base font-medium text-gray-900">{orderDetails.city}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bike Configuration */}
        {orderDetails?.model && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-200">
              <CreditCard className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-bold text-gray-900">Vehicle Configuration</h2>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">Model</p>
                <p className="text-base font-semibold text-gray-900">{orderDetails.model}</p>
              </div>
              {orderDetails.bikeCC && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Engine CC</p>
                  <p className="text-base font-semibold text-gray-900">{orderDetails.bikeCC} CC</p>
                </div>
              )}
              {orderDetails.batteryType && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Battery Type</p>
                  <p className="text-base font-semibold text-gray-900">{orderDetails.batteryType}</p>
                </div>
              )}
              {orderDetails.batteryCapacity && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Battery Capacity</p>
                  <p className="text-base font-semibold text-gray-900">{orderDetails.batteryCapacity}</p>
                </div>
              )}
              {orderDetails.quantity && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Quantity</p>
                  <p className="text-base font-semibold text-gray-900">{orderDetails.quantity}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Next Steps */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl shadow-lg p-6 mb-6 text-white">
          <h2 className="text-2xl font-bold mb-6">What Happens Next</h2>
          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center font-bold text-lg">
                1
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-2">Confirmation Email</h3>
              <p className="text-blue-100 text-sm">
                We've sent a confirmation email to {orderDetails?.email} with all order details.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid sm:grid-cols-3 gap-4 mb-6">
          <button
            onClick={() => navigate('/')}
            className="bg-green-600 text-white px-6 py-4 rounded-lg hover:bg-green-700 transition-all font-semibold shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
          >
            <Home className="w-5 h-5" />
            Homepage
          </button>
          <button
            onClick={() => navigate('/KgvkitOrder')}
            className="border-2 border-green-600 text-green-600 px-6 py-4 rounded-lg hover:bg-green-50 transition-all font-semibold flex items-center justify-center gap-2"
          >
            <RefreshCw className="w-5 h-5" />
            New Order
          </button>
          <button
            onClick={() => window.print()}
            className="border-2 border-gray-300 text-gray-700 px-6 py-4 rounded-lg hover:bg-gray-50 transition-all font-semibold flex items-center justify-center gap-2"
          >
            <Printer className="w-5 h-5" />
            Print Receipt
          </button>
        </div>

        {/* Support Info */}
        <div className="bg-white rounded-xl shadow-lg p-6 text-center">
          <h3 className="font-semibold text-gray-900 mb-3">Have Questions About Your Order?</h3>
          <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-600">
            <a href="mailto:team@kgvl.co.in" className="hover:text-blue-600 transition-colors">
              team@kgvl.co.in
            </a>
            <span className="text-gray-300">|</span>
            <span>+91 84479 61976</span>
            <span className="text-gray-300">|</span>
            <span>Mon-Sat: 9AM-6PM</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;